import { useState } from "react";
import { toast } from "sonner";
import { Image as ImageIcon, X, Upload, Video } from "lucide-react";
import { uploadFile, fileUrl } from "@/lib/api";

/** Uploader — multi image/video with previews */
export function MediaUploader({ value = [], onChange, label = "Photos / videos", accept = "image/*,video/*", max = 6, testIdPrefix = "media" }) {
  const [busy, setBusy] = useState(false);

  const handle = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    if (value.length + files.length > max) {
      toast.error(`You can upload up to ${max} files`);
      return;
    }
    setBusy(true);
    try {
      const uploaded = [];
      for (const f of files) {
        const r = await uploadFile(f);
        uploaded.push({ path: r.path, kind: r.kind });
      }
      onChange([...value, ...uploaded]);
      toast.success(`${uploaded.length} file(s) uploaded`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setBusy(false);
      e.target.value = "";
    }
  };

  const remove = (idx) => onChange(value.filter((_, i) => i !== idx));

  return (
    <div className="uploader" data-testid={`${testIdPrefix}-uploader`}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, flexWrap: "wrap" }}>
        <div>
          <b style={{ fontSize: 13 }}>{label}</b>
          <p style={{ margin: "2px 0 0", fontSize: 12, color: "var(--muted)" }}>
            {value.length}/{max} uploaded · Images up to 8MB, videos up to 40MB
          </p>
        </div>
        <label className="upload-btn">
          <Upload size={14} /> {busy ? "Uploading…" : "Add files"}
          <input type="file" accept={accept} multiple onChange={handle} disabled={busy} data-testid={`${testIdPrefix}-input`} />
        </label>
      </div>
      {value.length > 0 && (
        <div className="upload-grid">
          {value.map((m, i) => (
            <div className="upload-thumb" key={m.path} data-testid={`${testIdPrefix}-thumb-${i}`}>
              {m.kind === "video"
                ? <video src={fileUrl(m.path)} muted playsInline />
                : <img src={fileUrl(m.path)} alt="" />}
              <button type="button" className="remove" onClick={() => remove(i)} data-testid={`${testIdPrefix}-remove-${i}`} aria-label="Remove">
                <X size={12} />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/** Single-avatar uploader */
export function AvatarUploader({ value, onChange, testId = "avatar" }) {
  const [busy, setBusy] = useState(false);
  const handle = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setBusy(true);
    try {
      const r = await uploadFile(f);
      onChange(r.path);
      toast.success("Photo uploaded");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setBusy(false);
      e.target.value = "";
    }
  };
  return (
    <div className="avatar-uploader" data-testid={`${testId}-uploader`}>
      <div className="preview" data-testid={`${testId}-preview`}>
        {value ? <img src={fileUrl(value)} alt="Profile" /> : <ImageIcon size={32} />}
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        <b style={{ fontSize: 13 }}>Profile photo</b>
        <span style={{ fontSize: 12, color: "var(--muted)" }}>A clear headshot builds buyer trust. JPG/PNG, up to 8MB.</span>
        <label className="upload-btn" style={{ alignSelf: "flex-start" }}>
          <Upload size={14} /> {busy ? "Uploading…" : (value ? "Change photo" : "Upload photo")}
          <input type="file" accept="image/*" onChange={handle} disabled={busy} data-testid={`${testId}-input`} />
        </label>
        {value && (
          <button type="button" className="text-btn" style={{ textAlign: "left", padding: 0 }} onClick={() => onChange("")} data-testid={`${testId}-remove`}>
            Remove photo
          </button>
        )}
      </div>
    </div>
  );
}
